/**
 * @file QueueTable.jsx
 * @description QueueTable component for displaying and managing queue table.
 * @component QueueTable
 * @exports QueueTable - Main component for displaying and managing queue table.
 */

import React from "react";
import Box from '@mui/material/Box';
import Table from "@mui/material/Table";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { QUEUE_TABLE_COLUMN_KEYS, QUEUE_MESSAGES } from "../../../common/constants/ivrModule/queueConstants.js";

const QueueTable = ({
    page,
    order,
    setPage,
    orderBy,
    queue,
    rowsPerPage,
    columns,
    isQueueLoading,
    setRowsPerPage,
    onEdit,
    onDelete,
    handleRequestSort,
    filteredQueueLength
}) => {
    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    sx={{
                                        width:
                                            column.id === QUEUE_TABLE_COLUMN_KEYS.ACTIONS ? 50 :
                                            column.id === QUEUE_TABLE_COLUMN_KEYS.ID ? 150 :
                                            column.id === QUEUE_TABLE_COLUMN_KEYS.QUEUE_NAME ? 200 :
                                            column.id === QUEUE_TABLE_COLUMN_KEYS.DISTRIBUTION_POLICY_ID ? 200 :
                                            column.id === QUEUE_TABLE_COLUMN_KEYS.LABEL_ID ? 150 : 200,
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isQueueLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER} >
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : queue.length > 0 ? queue.map((queue) => (
                            <TableRow hover tabIndex={-1} key={queue.id}>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <IconButton onClick={() => onEdit(queue)}>
                                            <EditIcon fontSize={SIZES.SMALL} />
                                        </IconButton>
                                        <IconButton onClick={() => onDelete(queue)}>
                                            <DeleteIcon fontSize={SIZES.SMALL} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {queue.id}
                                </TableCell>
                                <TableCell>
                                    {queue.queuename}
                                </TableCell>
                                <TableCell>
                                    {queue.distributionPolicyId}
                                </TableCell>
                                <TableCell>
                                    {queue.labelId}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {QUEUE_MESSAGES.NO_QUEUE}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredQueueLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default QueueTable;